import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import axios from '@/lib/axios'
import { getErrorMessage } from '@/lib/utils'
import { ContentSettings } from '@/types/meta'

type ContentSettingsStateType = {
  isLoading: boolean
  response: ContentSettings | null
  error: {
    status?: number
    message: string
  } | null
}

type ContentSettingsStoreType = {
  contentSettingsState: ContentSettingsStateType
  getContentSettings: () => Promise<ContentSettings>
  updateContentSettings: (params: ContentSettings) => Promise<ContentSettings>
  reset: () => void
}

const initialState: ContentSettingsStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useContentSettingsStore = create<ContentSettingsStoreType>()(
  devtools(set => ({
    contentSettingsState: initialState,
    getContentSettings: async () => {
      set(() => ({
        contentSettingsState: {
          ...initialState,
          isLoading: true
        }
      }))

      try {
        const { data } = await axios.get('/admin/settings/content')
        set(() => ({
          contentSettingsState: {
            ...initialState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          contentSettingsState: {
            ...initialState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))

        throw error
      }
    },
    updateContentSettings: async (params: ContentSettings) => {
      set(() => ({
        contentSettingsState: {
          ...initialState,
          isLoading: true
        }
      }))

      try {
        const { data } = await axios.patch('/admin/settings/content', params)

        set(() => ({
          contentSettingsState: {
            ...initialState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          contentSettingsState: {
            ...initialState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))

        throw error
      }
    },
    reset: () => {
      set(() => ({
        contentSettingsState: initialState
      }))
    }
  }))
)

export default useContentSettingsStore
